import { coerceEmoji, toCategory } from "../model/category.js";
import { coerceNutrition } from "../model/nutrition.js";
import { coerceQuantity, parseQuantity } from "../model/quantity.js";
import { itemKey } from "../model/similar.js";
import { EMPTY_ENRICHMENT } from "./types.js";
/**
 * Turning a model's answer into the contract.
 *
 * Same posture as `extraction/parse.ts`: assume nothing survives contact. Every
 * field is validated independently and a bad one becomes null rather than
 * poisoning its neighbours, because the fields fail for unrelated reasons — a
 * hallucinated calorie count says nothing about whether the category is right.
 */
/**
 * The identity a product is cached under, globally.
 *
 * Built on `itemKey`, which is the module the whole app already uses to decide
 * two names are the same thing. That is deliberate and is the seam
 * `similar.ts` predicted in its own header: cache hits and duplicate detection
 * must agree, or the app would think two items are the same while charging for
 * two enrichments of them.
 *
 * Brand joins the key because "Amul milk" and "Nandini milk" are different
 * products with different panels — but a missing brand does NOT get a
 * placeholder, so plain "milk" keeps its own entry rather than colliding.
 */
export function productKey(name, brand) {
    const base = itemKey(name);
    const b = brand?.trim() ? itemKey(brand) : "";
    /**
     * Brand words the NAME already carries are not added again.
     *
     * A label scan yields name "Amul Milk" AND brand "Amul", while someone typing
     * yields just "amul milk". Naively joining the two gives "amul amul milk" for
     * the first and "amul milk" for the second — two keys for one product, so the
     * scanned carton misses the cache the typed one filled, and both get enriched.
     *
     * That is a silent cost and consistency bug rather than a visible one, which
     * is why it survived until a live probe printed the keys side by side.
     */
    const seen = new Set(base.split(" ").filter(Boolean));
    const brandWords = b.split(" ").filter((w) => w && !seen.has(w));
    const joined = [...brandWords, base].filter(Boolean).join(" ").trim();
    // A key must be a usable Firestore document id: non-empty, no slashes. itemKey
    // already strips punctuation, so the only real risk is emptiness — a name of
    // pure digits or noise words normalises away to nothing.
    const key = joined.replace(/\//g, " ").replace(/\s+/g, " ").trim();
    return key.length > 0 ? key.slice(0, 300) : "";
}
function clamp01(v) {
    const n = Number(v);
    return Number.isFinite(n) ? Math.min(1, Math.max(0, n)) : 0;
}
/**
 * One entry from the model.
 *
 * `ask` is passed so a size printed on the pack can win over the model's idea of
 * a typical carton. Evidence beats recall — the same rule the extraction layer
 * runs on, applied in the one place the two kinds of knowledge meet.
 */
export function coerceOne(raw, ask) {
    const o = (raw ?? {});
    const category = toCategory(o.category);
    // A size read off the pack is a fact about THIS packet; the model's amount is
    // a fact about packets in general. Prefer the fact.
    const fromPack = parseQuantity(ask?.sizeText);
    const quantity = fromPack ?? coerceQuantity(o.amount, o.unit);
    const nutrition = coerceNutrition({ kcal: o.kcal, protein: o.protein, carbs: o.carbs, fat: o.fat }, o.basis);
    return {
        category,
        emoji: coerceEmoji(o.emoji),
        quantity,
        // Nutrition on a non-food is a category error rather than a near miss, so
        // it is dropped even when the numbers themselves are well-formed.
        nutrition: category === "household" ? null : nutrition,
        confidence: clamp01(o.conf),
    };
}
/**
 * The whole batch, realigned to the asks.
 *
 * Indexed by the model's own `i` rather than by array position, because a model
 * that drops or reorders one item would otherwise shift every answer after it —
 * silently attaching milk's calories to the atta. Anything unmatched stays
 * empty, which is the honest result and still renders.
 */
export function coerceBatch(raw, asks) {
    const obj = (raw ?? {});
    // The model has been observed returning a bare array instead of the wrapper
    // object it was asked for. Both are unambiguous, so both are accepted.
    const list = Array.isArray(raw) ? raw : Array.isArray(obj.items) ? obj.items : [];
    const out = asks.map(() => ({ ...EMPTY_ENRICHMENT }));
    for (let pos = 0; pos < list.length; pos++) {
        const entry = (list[pos] ?? {});
        const claimed = Number(entry.i);
        // Fall back to position when `i` is missing or nonsense — a model that
        // simply answered in order is the common case and should still work.
        const idx = Number.isInteger(claimed) && claimed >= 0 && claimed < asks.length ? claimed : pos;
        if (idx >= asks.length)
            continue;
        out[idx] = coerceOne(entry, asks[idx]);
    }
    return out;
}
//# sourceMappingURL=coerce.js.map